<ng-container *transloco="let t">
  <!-- ══════════════════════════════════════════
       NAVBAR — frosted glass, scroll-aware
       ══════════════════════════════════════════ -->
  <header
    class="fixed top-0 left-0 w-full z-50 transition-all duration-500"
    [class.glass-nav]="isScrolled"
    [style.padding]="isScrolled ? '0.5rem 0' : '1rem 0'"
  >
    <div
      class="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between"
    >
      <!-- Logo -->
      <a
        href="#home"
        class="flex items-center gap-3 group"
        aria-label="Aurrica home"
      >
        <img
          [src]="themeService.isDarkMode() ? '/assets/images/logobwhd2.png' : '/assets/images/aurrica-logo.png'"
          class="h-9 w-auto transition-all duration-300 group-hover:opacity-80"
          alt="Aurrica"
          width="128"
          height="36"
        />
      </a>

      <!-- Desktop Nav Links -->
      <nav
        class="hidden lg:flex items-center gap-7 text-sm font-semibold"
        aria-label="Main navigation"
      >
        <a href="#home" class="nav-link">{{ t('navbar.home') }}</a>
        <a href="#about" class="nav-link">{{ t('navbar.about') }}</a>
        <a href="#features" class="nav-link">{{ t('navbar.features') }}</a>
        <a href="#download" class="nav-link">{{ t('navbar.download') }}</a>
        <a href="#faq" class="nav-link">{{ t('navbar.faq') }}</a>
        <a href="#contactUs" class="nav-link">{{ t('navbar.contact') }}</a>
      </nav>

      <!-- Right controls -->
      <div class="flex items-center gap-3">
        <!-- Language selector -->
        <div class="relative">
          <button
            type="button"
            (click)="toggleLanguageDropdown()"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
            style="color: var(--nav-text)"
            [attr.aria-expanded]="isLanguageDropdownOpen"
            aria-haspopup="true"
          >
            <img
              [src]="selectedLanguage.flag"
              [alt]="selectedLanguage.code"
              class="w-4 h-4 rounded-full object-cover"
            />
            <span class="hidden sm:inline"
              >{{ t(selectedLanguage.label) }}</span
            >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-3.5 h-3.5 transition-transform duration-200"
              [class.rotate-180]="isLanguageDropdownOpen"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          @if (isLanguageDropdownOpen) {
          <div
            role="button"
            tabindex="-1"
            class="fixed inset-0 z-10"
            (click)="closeLanguageDropdown()"
          ></div>
          <div
            class="absolute right-0 mt-2 w-36 rounded-xl overflow-hidden z-20 glass-strong py-1"
            role="menu"
          >
            @for (lang of languages; track lang.code) {
            <button
              type="button"
              (click)="changeLanguage(lang)"
              class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left transition-colors"
              style="color: var(--nav-text)"
              role="menuitem"
            >
              <img
                [src]="lang.flag"
                [alt]="lang.code"
                class="w-4 h-4 rounded-full object-cover"
              />
              {{ t(lang.label) }}
            </button>
            }
          </div>
          }
        </div>

        <!-- Theme toggle button -->
        <button
          type="button"
          class="theme-btn"
          (click)="themeService.toggleDarkMode()"
          [attr.aria-label]="themeService.isDarkMode() ? 'Switch to light mode' : 'Switch to dark mode'"
          title="Toggle theme"
        >
          @if (themeService.isDarkMode()) {
            <!-- Sun icon — switch to light -->
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
          } @else {
            <!-- Moon icon — switch to dark -->
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
            </svg>
          }
        </button>

        <!-- Desktop CTA -->
        <a
          [href]="freightLoginUrl"
          target="_blank"
          rel="noopener"
          class="hidden lg:inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold text-white transition-all duration-300"
          style="background: linear-gradient(135deg, #008cf0, #0052a3)"
        >
          Get Started
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M7 17L17 7M17 7H7M17 7v10"
            />
          </svg>
        </a>

        <!-- Mobile burger -->
        <button
          type="button"
          class="lg:hidden w-9 h-9 flex flex-col justify-center items-center gap-1.5 rounded-lg transition-colors"
          (click)="toggleMobileMenu()"
          [attr.aria-expanded]="isMobileMenuOpen"
          aria-label="Toggle menu"
        >
          <span
            class="block w-5 h-0.5 rounded-full transition-all duration-300 origin-center"
            style="background: var(--nav-text)"
            [style.transform]="isMobileMenuOpen ? 'translateY(5px) rotate(45deg)' : 'none'"
          ></span>
          <span
            class="block w-5 h-0.5 rounded-full transition-all duration-300"
            style="background: var(--nav-text)"
            [style.opacity]="isMobileMenuOpen ? '0' : '1'"
          ></span>
          <span
            class="block w-5 h-0.5 rounded-full transition-all duration-300 origin-center"
            style="background: var(--nav-text)"
            [style.transform]="isMobileMenuOpen ? 'translateY(-5px) rotate(-45deg)' : 'none'"
          ></span>
        </button>
      </div>
    </div>

    <!-- Mobile Menu Panel -->
    @if (isMobileMenuOpen) {
    <div
      class="lg:hidden glass-nav border-t border-white/5 mt-2 px-6 py-4 space-y-1"
      role="dialog"
      aria-modal="true"
    >
      <a href="#home" (click)="closeMobileMenu()" class="mobile-nav-link"
        >{{ t('navbar.home') }}</a
      >
      <a href="#about" (click)="closeMobileMenu()" class="mobile-nav-link"
        >{{ t('navbar.about') }}</a
      >
      <a href="#features" (click)="closeMobileMenu()" class="mobile-nav-link"
        >{{ t('navbar.features') }}</a
      >
      <a href="#download" (click)="closeMobileMenu()" class="mobile-nav-link"
        >{{ t('navbar.download') }}</a
      >
      <a href="#faq" (click)="closeMobileMenu()" class="mobile-nav-link"
        >{{ t('navbar.faq') }}</a
      >
      <a href="#contactUs" (click)="closeMobileMenu()" class="mobile-nav-link"
        >{{ t('navbar.contact') }}</a
      >

      <div class="pt-3">
        <a
          [href]="freightLoginUrl"
          target="_blank"
          rel="noopener"
          class="btn-primary w-full justify-center"
        >
          Get Started
        </a>
      </div>
    </div>
    }
  </header>
</ng-container>
